import React, { useEffect, useState } from 'react';
import {
    Box,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Fade,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { BulkActions, Confirmation, SkeletonContent } from './';
import { TypeRow } from './TypeRow';
import {
    IBulkActions,
    IConfirmation,
    ISkeletonContent,
    ITable,
    TColumItem,
} from './types';
import useAuth from 'src/hooks/useAuth';
import { shouldRenderCell } from '../utils/shouldRenderCell';

const useStyles = makeStyles(() => ({
    root: {},
    row: { height: 40 },
    cell: {
        overflow: 'hidden',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
    },
}));

export const Table = ({
    items = [],
    columns,
    module,
    links = [],
    handleDelete,
    query,
    loading,
    limit,
    canDelete,
    visibleRows,
    ...props
}: ITable) => {
    const classes = useStyles();
    const { user } = useAuth();
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        setSelectedItems([]);
    }, [query]);

    const { t } = useTranslation();

    const handleSelectAll = (event: any) =>
        setSelectedItems(
            event.target.checked ? items.map((item: any) => item._id) : []
        );

    const handleSelectOne = (event: any, itemId: string) => {
        if (!selectedItems.includes(itemId))
            setSelectedItems((prevSelected) => [...prevSelected, itemId]);
        else
            setSelectedItems((prevSelected) =>
                prevSelected.filter((id) => id !== itemId)
            );
    };

    const selectedSome =
        selectedItems?.length > 0 && selectedItems?.length < items?.length;

    const selectedAll =
        selectedItems?.length === items?.length && items?.length > 0;

    const isBulkActions = selectedItems.length > 0;

    const handleConfirm = (response: boolean) => {
        setOpen(false);
        if (response) {
            setSelectedItems([]);
            handleDelete(selectedItems);
        }
    };

    const handleOpen = () => setOpen(true);

    const BULK_ACTIONS_PROPS: IBulkActions = {
        handleOpen,
        handleSelectAll,
        selectedSome,
        selectedAll,
        selectedItems,
    };

    const CONFIRMATION_PROPS: IConfirmation = {
        open,
        setOpen,
        message: t('delete-confirmation').replace(
            '{size}',
            String(selectedItems.length)
        ),
        handleConfirm,
    };

    const SKELETON_CONTENT_PROPS: ISkeletonContent = {
        columns,
        size: limit,
        canDelete,
    };

    return (
        <Box {...props}>
            <Confirmation {...CONFIRMATION_PROPS} />
            {isBulkActions && <BulkActions {...BULK_ACTIONS_PROPS} />}
            <MuiTable className={classes.root}>
                <TableHead>
                    <TableRow>
                        {canDelete && (
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={selectedAll}
                                    indeterminate={selectedSome}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                        )}
                        {columns.map((column, key) => {
                            if (
                                !shouldRenderCell({
                                    column,
                                    user,
                                })
                            ) {
                                return null;
                            }
                            if (visibleRows && !visibleRows[column.field]) {
                                return null;
                            }
                            return (
                                <TableCell
                                    key={key}
                                    style={{
                                        maxWidth: column.width || 'inherit',
                                        width: column.width || 'inherit',
                                    }}
                                    className={classes.cell}
                                >
                                    {t(`${module.name}.${column.field}`)}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <SkeletonContent {...SKELETON_CONTENT_PROPS} />
                    ) : (
                        items?.map((item: TColumItem, index: number) => {
                            const isItemSelected = selectedItems.includes(
                                item._id
                            );

                            return (
                                <Fade
                                    in={!loading}
                                    key={item._id}
                                    timeout={1000}
                                >
                                    <TableRow
                                        hover
                                        key={item._id}
                                        selected={isItemSelected}
                                        className={classes.row}
                                    >
                                        {canDelete && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    onChange={(event) =>
                                                        handleSelectOne(
                                                            event,
                                                            item._id
                                                        )
                                                    }
                                                    value={isItemSelected}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column, key) => {
                                            if (
                                                !shouldRenderCell({
                                                    column,
                                                    user,
                                                })
                                            ) {
                                                return null;
                                            }
                                            if (visibleRows && !visibleRows[column.field]) {
                                                return null;
                                            }
                                            return (
                                                <TableCell
                                                    key={key}
                                                    style={{
                                                        maxWidth:
                                                            column.width ||
                                                            'inherit',
                                                        width:
                                                            column.width ||
                                                            'inherit',
                                                    }}
                                                    className={classes.cell}
                                                >
                                                    <TypeRow
                                                        column={column}
                                                        item={item}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </Fade>
                            );
                        })
                    )}
                </TableBody>
            </MuiTable>
        </Box>
    );
};
